import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess } from '@/context/auth/types';
import { LibraryTab } from './tabs/LibraryTab';
import { UploadTab } from './tabs/UploadTab';
import { GenerateTab } from './tabs/GenerateTab';
import { useSearchParams } from 'react-router-dom';
import { Image as ImageIcon } from 'lucide-react';

interface MediaDashboardProps {
  initialTab?: string;
}

const MediaDashboard: React.FC<MediaDashboardProps> = ({ initialTab }) => {
  const { user } = useAuth();
  const isSuperAdmin = user ? hasSuperAdminAccess(user.role) : false;

  // URL-based tab state
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  // Default to library, and redirect from generate tab if not super admin
  const defaultTab = initialTab || tabFromUrl || 'library';
  const validTab = defaultTab === 'generate' && !isSuperAdmin ? 'library' : defaultTab;
  const [activeTab, setActiveTab] = useState(validTab);

  // Update active tab when URL parameters change
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      // Prevent non-super admins from accessing generate tab via URL
      if (tabFromUrl === 'generate' && !isSuperAdmin) {
        setActiveTab('library');
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', 'library');
        setSearchParams(newSearchParams);
      } else {
        setActiveTab(tabFromUrl);
      }
    }
  }, [tabFromUrl, isSuperAdmin]);

  // Update active tab when initialTab prop changes
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      // Prevent non-super admins from accessing generate tab via prop
      if (initialTab === 'generate' && !isSuperAdmin) {
        setActiveTab('library');
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', 'library');
        setSearchParams(newSearchParams);
      } else {
        setActiveTab(initialTab);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', initialTab);
        setSearchParams(newSearchParams);
      }
    }
  }, [initialTab, isSuperAdmin, setSearchParams]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', value);
    setSearchParams(newSearchParams);
  };

  return (
    <div className="space-y-6 min-h-[calc(100vh+1px)] w-full max-w-full overflow-hidden">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <ImageIcon className="w-6 h-6 text-primary" />
                <CardTitle>Media Library</CardTitle>
              </div>
              <CardDescription>
                Upload, generate, and manage images for your campaigns
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className={`${isSuperAdmin ? 'grid grid-cols-3' : 'grid grid-cols-2'} gap-1.5 sm:flex sm:flex-wrap h-auto w-full mb-4`}>
              <TabsTrigger value="library" className="sm:flex-[1_0_21%] lg:flex-[1_0_18%]">
                Library
              </TabsTrigger>
              <TabsTrigger value="upload" className="sm:flex-[1_0_21%] lg:flex-[1_0_18%]">
                Upload
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger value="generate" className="sm:flex-[1_0_21%] lg:flex-[1_0_18%]">
                  Generate
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="library">
              <LibraryTab />
            </TabsContent>

            <TabsContent value="upload">
              <UploadTab />
            </TabsContent>

            {isSuperAdmin && (
              <TabsContent value="generate">
                <GenerateTab />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default MediaDashboard;
